import { useTranslation } from "@i18n";
import DateTimeComponent, { type DateTimeChangeArgs } from "@CubeComponents/Mui_Form/DateTimeComponent";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { getFileImage } from "@Services/utils/iconMap";
import dayjs from "dayjs";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { Chip } from "@mui/material";
import type {
  AutocompleteInputChangeReason,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import type React from "react";
import { useState } from "react";
import "@Assets/css/colors.css";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import Text from "@CubeComponents/html/Text/Text";
import type { MailDataState, FrontendAttachment, FrontendReference } from "./mailbox.interfaces";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";

interface MailContentProps {
  mailData: Pick<MailDataState, "body" | "attachments" | "references" | "dueDate" | "responseType" | "tags">;
  setMailData: React.Dispatch<React.SetStateAction<MailDataState>>;
  setOpenAttachmentsModal: (open: boolean) => void;
  setOpenReferencesModal: (open: boolean) => void;
}

/** Option shape for the response-type SelectDropDown. */
type ResponseTypeOption = {
  name: string;
  value: string;
};

function MailContent({ mailData, setMailData, setOpenAttachmentsModal, setOpenReferencesModal }: MailContentProps) {
  const [inputValue, setInputValue] = useState("");
  const { t } = useTranslation();
  const responseTypes: ResponseTypeOption[] = [
    { name: "Acknowledge By", value: "ACKNOWLEDGE_BY" },
    { name: "Reply By", value: "REPLY_BY" },
  ];

  const handleRemoveAttachment = (index: number) => {
    const attachments = [...mailData.attachments];
    attachments.splice(index, 1);
    setMailData((prev: MailDataState) => ({ ...prev, attachments }));
  };
  const handleRemoveReferenceFile = (index: number) => {
    const references = mailData.references;
    references.splice(index, 1);
    setMailData((prev: MailDataState) => ({ ...prev, references }));
  };

  const handleDateChange = (e: DateTimeChangeArgs) => {
    setMailData((prev: MailDataState) => ({ ...prev, dueDate: e.value?.getTime() ?? null }));
  };
  const handleResponseTypeChange = (option: Record<string, unknown> | Record<string, unknown>[] | null) => {
    // Single-select, so one option object or null comes back; only `value` is read off it, and it is
    // a string on both `responseTypes` entries. Read rather than asserted, so an unexpected shape
    // clears the field instead of storing a non-string.
    const value = !Array.isArray(option) && typeof option?.value === "string" ? option.value : "";
    setMailData((prev: MailDataState) => ({ ...prev, responseType: value }));
  };

  const handleChangeTags = (event: React.SyntheticEvent, newValue: string[]) => {
    const uniqueValues = newValue;
    setMailData((prev: MailDataState) => ({
      ...prev,
      tags: uniqueValues,
    }));
    setInputValue("");
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "input") {
      if (newInputValue.endsWith(",")) {
        setInputValue("");
        handleChangeTags(event, [...mailData.tags, newInputValue.slice(0, -1).trim()]);
      } else {
        setInputValue(newInputValue);
      }
    }
    if (["reset", "clear"].includes(reason)) return setInputValue("");
  };

  const getTagColor = (tag: string) => {
    if (!tag) return `var(--system-colour-1)`;

    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.codePointAt(i)! + ((hash << 5) - hash);
    }

    const colorIndex = (Math.abs(hash) % 46) + 1;
    return `var(--system-colour-${colorIndex})`;
  };

  return (
    <>
      <div className="mt-10" data-testid="mailTextEditor">
        <SimpleEditor
          showCharCount={true}
          showToolbar={true}
          value={sanitizeHtml(mailData.body)}
          onChange={(html: string) => setMailData((prev: MailDataState) => ({ ...prev, body: html }))}
          height={200}
        />
      </div>

      <div className="flex flex-1 items-center my-5 w-full gap-2">
        <div className="flex-0 w-28 whitespace-nowrap" data-testid="addAttachmentsToMail">
          <Text>{t("Attachments")}</Text>
        </div>
        <div className="flex-1">
          <ButtonComponent
            className="rounded px-2 py-1 flex items-center justify-center"
            buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
            onClick={() => setOpenAttachmentsModal(true)}
            data-testid="addAttachmentsBtn"
          >
            <IconComponent name={"Add-Create"} className="w-4 h-4 mt-1" />
          </ButtonComponent>
        </div>
      </div>

      {mailData.attachments.length ? (
        <div className="flex flex-row flex-wrap pe-3 overflow-auto gap-2 w-full h-full">
          {mailData.attachments.map((data: FrontendAttachment, index: number) => {
            // `getFileImage` is declared `any` but every branch of it returns a URL string: the
            // `.svg` imports it resolves carry no `?react` suffix, and vite-plugin-svgr v5 only
            // componentises those. The old `typeof … === "string"` branch here was the last place
            // treating it as a component, and iconMap.ts already records that as dead.
            const fileImage: string = getFileImage(data.attachmentsData);
            return (
              <span key={index} className="flex border-solid border-2 rounded-lg px-1 items-center">
                {/* <span>{data.awsKey && handleFileOpen(data.awsKey, index)}</span> */}
                <span className="w-10 h-8 flex object-contain">
                  <img src={fileImage} alt={t("Attachment")} />
                </span>
                <span className="w-full text-sm">{data.attachmentsData.name}</span>
                <span className="w-6">
                  <IconComponent
                    className="cursor-pointer w-4 h-4"
                    onClick={() => handleRemoveAttachment(index)}
                    name="close"
                    width="0.9375rem"
                    height="0.9375rem"
                  />
                </span>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="flex flex-1 items-center my-5 w-full gap-2">
        <div className="flex-0 w-28 whitespace-nowrap" data-testid="addReferencesToMail">
          <Text>{t("References")}</Text>
        </div>
        <div className="flex-1">
          <ButtonComponent
            className="rounded px-2 py-1"
            buttonType={BUTTON_TYPES.INFO_BACKGROUND_BUTTON}
            onClick={() => setOpenReferencesModal(true)}
            data-testid="addReferencesBtn"
          >
            <IconComponent name={"Add-Create"} className="w-4 h-4 mt-1" />
          </ButtonComponent>
        </div>
      </div>

      {mailData.references.length ? (
        <div className="flex flex-row flex-wrap pe-3 overflow-auto gap-2 w-full h-full">
          {mailData.references.map((data: FrontendReference, index: number) => {
            // See the attachments list above: `getFileImage` always resolves to a URL string.
            const fileImage: string = getFileImage(data);
            return (
              <span key={index} className="flex border-solid border-2 rounded-lg px-1 items-center">
                {/* <span>{data.awsKey && handleFileOpen(data.awsKey, index)}</span> */}
                <span className="w-10 h-8 flex object-contain">
                  <img src={fileImage} alt={t("Reference")} className="w-14 p-1" />
                </span>
                <span className="w-full text-sm">{data.name}</span>
                <span className="w-6">
                  <IconComponent
                    className="cursor-pointer"
                    onClick={() => handleRemoveReferenceFile(index)}
                    name="close"
                    width="0.9375rem"
                    height="0.9375rem"
                  />
                </span>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="flex items-center w-full gap-4">
        <div className="flex flex-1 items-center my-5 w-full gap-2">
          <div className="flex-0 w-28 whitespace-nowrap" data-testid="responseTypeText">
            <Text>{t("Response Type")}</Text>
          </div>
          <div className="flex-1" data-testid="responseTypeDropdown">
            <SelectDropDown
              options={responseTypes}
              fields={{ text: "name", value: "value" }}
              placeholder={t("Select Response Type")}
              value={responseTypes.find((r) => r.value === mailData.responseType) ?? null}
              onChange={handleResponseTypeChange}
            />
          </div>
        </div>
        <div className="flex flex-1 items-center my-5 w-full gap-2">
          <div className="flex-0 me-2 whitespace-nowrap" data-testid="dueDateText">
            <Text>{t("Due Date")}</Text>
          </div>
          <div className="flex-1 rounded-sm" data-testid="datePickerComponent">
            <DateTimeComponent
              width="100%"
              id="datepicker"
              placeholder={t("Enter date")}
              min={dayjs().toDate()}
              onChange={handleDateChange}
              sx={{ borderRadius: "0.5rem" }}
              format="dd/MM/yyyy HH:mm"
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-28">
          <Text>{t("Tags")}</Text>
        </div>
        <div className="ms-1 w-full">
          {" "}
          <AutoComplete
            multiple
            freeSolo
            options={[]}
            value={mailData.tags}
            onChange={(event: React.SyntheticEvent, newValue: string[], reason: AutocompleteChangeReason) => {
              handleChangeTags(event, newValue);
              if (reason === "createOption") {
                setInputValue("");
              }
            }}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            filterSelectedOptions
            renderValue={(value: string[], getItemProps) =>
              value.map((option: string, index: number) => {
                const { key, ...tagProps } = getItemProps({ index });
                return (
                  <Chip
                    deleteIcon={<IconComponent name="crossWhite" width="1rem" height="1rem" />}
                    key={key}
                    variant="outlined"
                    sx={{
                      backgroundColor: getTagColor(option),
                      color: "white",
                      paddingX: "0.5rem",
                      gap: "0.5rem",
                    }}
                    label={option}
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldComponent
                {...params}
                sx={{ paddingY: "0.125rem" }}
                variant="outlined"
                onBlur={(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  const tag = e.target.value.trim();
                  if (tag) {
                    handleChangeTags(e, [...mailData.tags, tag]);
                  }
                }}
                placeholder={mailData.tags.length ? "" : t("Add tags")}
              />
            )}
          />
        </div>
      </div>
    </>
  );
}

export default MailContent;
